// src/lib/tier.ts
//
// Frontend mirror of the backend pricing-tier matrix
// (backend/services/tier_gating.py). This drives UI rendering of locked vs.
// unlocked features ONLY — the backend remains DB-authoritative for enforcement
// (every gated route 403s `FEATURE_NOT_IN_TIER` regardless of what the UI shows).
//
// Matrix locked by Mosta 2026-06-05: AI Payroll is all-tier, Analytics & Offers is
// Performance+, Customer CRM is Power-only. Keep this in sync with the backend enum.

export type Tier = "essentials" | "performance" | "power";

export type Feature =
  | "pnl_bookkeeping"
  | "smart_inventory"
  | "auto_reorder"
  | "invoice_upload"
  | "insights_agent"
  | "ai_payroll"
  | "performance_menu"
  | "dynamic_pricing"
  | "channel_pnl"
  | "catering"
  | "customer_crm"
  | "loyalty_sms"
  | "reviews"
  | "market_intelligence"
  | "restaurant_website";

const ESSENTIALS_FEATURES: Feature[] = [
  "pnl_bookkeeping",
  "smart_inventory",
  "auto_reorder",
  "invoice_upload",
  "insights_agent",
  "ai_payroll",
];

const PERFORMANCE_ADDS: Feature[] = [
  "performance_menu",
  "dynamic_pricing",
  "channel_pnl",
  "catering",
];

const POWER_ADDS: Feature[] = [
  "customer_crm",
  "loyalty_sms",
  "reviews",
  "market_intelligence",
  "restaurant_website",
];

// Built via spread so tier inheritance is correct by construction.
export const TIER_FEATURES: Record<Tier, ReadonlySet<Feature>> = {
  essentials: new Set(ESSENTIALS_FEATURES),
  performance: new Set([...ESSENTIALS_FEATURES, ...PERFORMANCE_ADDS]),
  power: new Set([...ESSENTIALS_FEATURES, ...PERFORMANCE_ADDS, ...POWER_ADDS]),
};

const TIER_ORDER: Tier[] = ["essentials", "performance", "power"];

function isTier(value: unknown): value is Tier {
  return value === "essentials" || value === "performance" || value === "power";
}

/** True if a merchant on `tier` is entitled to `feature`. Fail-closed on unknown tier. */
export function hasFeature(tier: Tier | null | undefined, feature: Feature): boolean {
  if (!isTier(tier)) return false;
  return TIER_FEATURES[tier].has(feature);
}

/** Lowest tier that includes `feature` — for upgrade copy ("Upgrade to Power"). */
export function minTierForFeature(feature: Feature): Tier | null {
  for (const t of TIER_ORDER) {
    if (TIER_FEATURES[t].has(feature)) return t;
  }
  return null;
}

export const TIER_LABEL: Record<Tier, string> = {
  essentials: "Essentials",
  performance: "Performance",
  power: "Power",
};

export const FEATURE_LABEL: Record<Feature, string> = {
  pnl_bookkeeping: "P&L & Bookkeeping",
  smart_inventory: "Smart Inventory",
  auto_reorder: "Auto Reorder",
  invoice_upload: "Invoice Upload",
  insights_agent: "AI Insights Agent",
  ai_payroll: "Payroll & Scheduling",
  performance_menu: "Menu Performance",
  dynamic_pricing: "Dynamic Pricing",
  channel_pnl: "Channel-Aware P&L",
  catering: "Catering Profitability",
  customer_crm: "Customer Intelligence CRM",
  loyalty_sms: "Loyalty & SMS Marketing",
  reviews: "Reviews & Online Presence",
  market_intelligence: "Market Intelligence",
  restaurant_website: "Restaurant Website",
};
